// Environment utility functions for handling different deployment environments
// This ensures OG tags and URLs work correctly across QA, staging, and production

/**
 * Get the base URL for the current environment
 * @returns {string} The base URL for the current environment
 */
export const getBaseUrl = () => {
  // Use environment variable for consistent URL across all environments
  // This ensures social media crawlers get the correct URL without JavaScript
  if (process.env.REACT_APP_FRONTEND_URL) {
    return process.env.REACT_APP_FRONTEND_URL;
  }

  // Fallback for development if environment variable is not set
  return 'https://glammify.ai';
};

/**
 * Get the OG image URL for the current environment
 * @returns {string} The absolute URL to the OG image
 */
export const getOGImageUrl = () => {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/assets/images/glammify-og-image.png`;
};

/**
 * Get the absolute URL for any asset path
 * @param {string} assetPath - The asset path (e.g., '/assets/images/glammify-og-image.png')
 * @returns {string} The absolute URL to the asset
 */
export const getAbsoluteAssetUrl = (assetPath) => {
  const baseUrl = getBaseUrl();
  // Remove leading slash if present
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Get the register page URL for the current environment
 * @param {string} referralCode - Optional referral code to append
 * @returns {string} The absolute URL to the register page
 */
export const getRegisterUrl = (referralCode = null) => {
  const baseUrl = getBaseUrl();
  const registerUrl = `${baseUrl}/register`;
  
  if (referralCode) {
    return `${registerUrl}?ref=${referralCode}`;
  }
  
  return registerUrl;
};

/**
 * Get environment-specific configuration
 * @returns {object} Environment configuration object
 */
export const getEnvironmentConfig = () => {
  const baseUrl = getBaseUrl();
  
  return {
    baseUrl,
    ogImageUrl: getOGImageUrl(),
    registerUrl: getRegisterUrl(),
    isProduction: baseUrl.includes('glammify.ai'),
    isQA: baseUrl.includes('glammifyqa.datagrid.co.in'),
    isDevelopment: baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')
  };
};

export default {
  getBaseUrl,
  getOGImageUrl,
  getRegisterUrl,
  getEnvironmentConfig
};
